import { useMemo, useState, useEffect, useCallback } from 'react';
import {
	type JSMProjectTemplateKey,
	INITIAL_PROJECT_TEMPLATE_PROJECT_PROPERTY_KEY,
	IT_SERVICE_MANAGEMENT_COMPANY_MANAGED_KEY,
} from '@atlassian/jira-common-constants/src/project-template-type-keys';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useProjectProperties } from '@atlassian/jira-project-properties-services/src/services/index.tsx';
import { useProjectContext } from '@atlassian/jira-providers-project-context';
import type { UseResourceCustomContext } from '@atlassian/jira-resource-with-custom-router-context/src/controllers/types.tsx';
import { useResourceWithCustomRouterContext } from '@atlassian/jira-resource-with-custom-router-context/src/controllers/use-resource-with-custom-router-context/index.tsx';
import {
	requiredFrontendProperties,
	requiredProjectProperties,
} from '@atlassian/jira-router-resources-service-desk-project-getting-started/src/services/index.tsx';

import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import {
	DISABLE_NPLE_PROJECT_PROJECT_KEY,
	GETTING_STARTED_PAGE_EXCLUDED_PROJECT_TEMPLATES_FOR_ITSM,
	GETTING_STARTED_PAGE_EXCLUDED_PROJECT_TEMPLATES_FOR_PROD,
	PACKAGE_NAME,
	PACKAGE_TEAM_NAME,
} from '../common/constants';
import type {
	GettingStartedPageStateProps,
	ExcludedGettingStartedPageTemplatesType,
} from '../common/types';
import { useFirstProjectDetails } from './first-project-state';
import {
	getLandingPageState,
	initialiseLandingPageState,
	updateLandingPageStateProperties,
} from './getting-started-page-state';

export const useGettingStartedPageStateActions = () => {
	const baseUrl = window.location.origin;
	const { cloudId } = useTenantContext();
	const { data } = useProjectContext() || {};
	const { projectId } = data || {};
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const projectIdString = projectId ? projectId.toString() : '';

	return useMemo(() => {
		const actionProps: GettingStartedPageStateProps = {
			baseUrl,
			cloudId,
			projectId: projectIdString,
			createAnalyticsEvent,
		};
		return [
			{
				updateLandingPageStateProperties: updateLandingPageStateProperties(actionProps),
				getLandingPageState: getLandingPageState(actionProps),
				initialiseLandingPageState: initialiseLandingPageState(actionProps),
			},
		];
	}, [baseUrl, cloudId, createAnalyticsEvent, projectIdString]);
};

export const useIsFirstProjectITSM = (inputProjectId?: string) => {
	const { data: containerContext, loading } = useProjectContext();
	const currentProjectId = inputProjectId || containerContext?.projectId;
	const [isFirstProject, setIsFirstProject] = useState(false);
	const [isFirstProjectItsm, setIsFirstProjectItsm] = useState(false);
	const [isStateLoading, setStateLoading] = useState(true);
	const { error: isFirstProjectError, firstProject } = useFirstProjectDetails();

	useEffect(() => {
		if (currentProjectId && !isFirstProjectError && firstProject) {
			setIsFirstProject(firstProject.projectId === `${currentProjectId}`);
			setIsFirstProjectItsm(
				firstProject.projectTemplateKey === IT_SERVICE_MANAGEMENT_COMPANY_MANAGED_KEY,
			);
			setStateLoading(false);
		} else if (isFirstProjectError) {
			setStateLoading(false);
		}
	}, [currentProjectId, firstProject, isFirstProjectError, setStateLoading]);

	const memoizedValue = useMemo(
		() => ({
			isLoading: loading || fg('ssr_nav4_queues_sidebar') ? false : isStateLoading,
			isFirstProject,
			isFirstProjectItsm,
			isFirstProjectError,
		}),
		[isFirstProject, isFirstProjectItsm, isFirstProjectError, loading, isStateLoading],
	);
	return memoizedValue;
};

const useInitialProjectTemplateProjectPropertyKeyWithFF = (
	projectId?: string,
	customContext?: UseResourceCustomContext,
): [null | string | undefined, boolean] => {
	const isSsrNav4QueuesSidebarProductionised = fg('ssr_nav4_queues_sidebar');
	if (isSsrNav4QueuesSidebarProductionised) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const { data, loading } = useResourceWithCustomRouterContext(
			requiredFrontendProperties,
			customContext,
		);
		if (!data) {
			return [null, loading];
		}
		return [data[INITIAL_PROJECT_TEMPLATE_PROJECT_PROPERTY_KEY], loading];
	}
	const { [INITIAL_PROJECT_TEMPLATE_PROJECT_PROPERTY_KEY]: initialProjectTemplateOld } =
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useProjectProperties<{
			[INITIAL_PROJECT_TEMPLATE_PROJECT_PROPERTY_KEY]: JSMProjectTemplateKey;
		}>([INITIAL_PROJECT_TEMPLATE_PROJECT_PROPERTY_KEY], projectId);

	const isLoadingOld = initialProjectTemplateOld === undefined;
	return [initialProjectTemplateOld, isLoadingOld];
};

export const useIsGettingStartedPageExcludedProject = (
	projectId?: string,
	customContext?: UseResourceCustomContext,
): [boolean, boolean] => {
	const [initialProjectTemplate, isLoading] = useInitialProjectTemplateProjectPropertyKeyWithFF(
		projectId,
		customContext,
	);
	const {
		isLoading: isFirstProjectITSMLoading,
		isFirstProject,
		isFirstProjectItsm,
	} = useIsFirstProjectITSM(projectId);
	const excludedProjectTemplates =
		isFirstProject &&
		isFirstProjectItsm &&
		expValEquals('jsm_getting_started_page_for_nple_exp', 'cohort', 'variation')
			? GETTING_STARTED_PAGE_EXCLUDED_PROJECT_TEMPLATES_FOR_ITSM
			: GETTING_STARTED_PAGE_EXCLUDED_PROJECT_TEMPLATES_FOR_PROD;

	return useMemo(
		() => [
			isLoading || isFirstProjectITSMLoading,
			excludedProjectTemplates.includes(
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				initialProjectTemplate as ExcludedGettingStartedPageTemplatesType,
			),
		],
		[excludedProjectTemplates, initialProjectTemplate, isFirstProjectITSMLoading, isLoading],
	);
};

export const useGetNpleDisabledProjectProperty = (projectKeyOrId: number | string | undefined) => {
	const [npleDisabled, setNpleDisabled] = useState<boolean | undefined>(
		projectKeyOrId ? undefined : true,
	);
	const [isLoadingNew, setIsLoadingNew] = useState(true);

	const isLoading = npleDisabled === undefined;
	const getNpleDisabledProjectProperty = useCallback(async () => {
		try {
			const response = await performGetRequest(
				`/rest/api/3/project/${projectKeyOrId}/properties/${DISABLE_NPLE_PROJECT_PROJECT_KEY}`,
			);
			setNpleDisabled(response.value);
			setIsLoadingNew(false);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			setNpleDisabled(error.statusCode !== 404);
			setIsLoadingNew(false);
			error.statusCode !== 404 &&
				fireErrorAnalytics({
					meta: {
						id: 'getDisableNpleProjectProperty',
						packageName: PACKAGE_NAME,
						teamName: PACKAGE_TEAM_NAME,
					},
					error,
					sendToPrivacyUnsafeSplunk: true,
				});
		}
	}, [projectKeyOrId]);

	useEffect(() => {
		if (projectKeyOrId) {
			getNpleDisabledProjectProperty();
		}
	}, [getNpleDisabledProjectProperty, projectKeyOrId]);

	const isLoadingValue = fg('jsm-wait-for-project-context-in-nple') ? isLoadingNew : isLoading;

	return [isLoadingValue, npleDisabled];
};

const useGetNpleDisabledProjectPropertyWithFF = (
	projectKey?: string,
	customContext?: UseResourceCustomContext,
) => {
	const isNpleProductionised = ff('getting_started_page_production_rollout');
	const isSsrNav4QueuesSidebarProductionised = fg('ssr_nav4_queues_sidebar');

	const [isGetNpleDisabledLoadingOld, npleDisabledOld] = useGetNpleDisabledProjectProperty(
		isNpleProductionised && !isSsrNav4QueuesSidebarProductionised ? projectKey : undefined,
	);

	const { data: requiredProjectPropertiesDataNew, loading: isGetNpleDisabledLoadingNew } =
		useResourceWithCustomRouterContext(requiredProjectProperties, customContext);

	const npleDisabledNew = requiredProjectPropertiesDataNew
		? requiredProjectPropertiesDataNew[DISABLE_NPLE_PROJECT_PROJECT_KEY]
		: true;

	const npleDisabled = isSsrNav4QueuesSidebarProductionised ? npleDisabledNew : npleDisabledOld;

	const isGetNpleDisabledLoading = isSsrNav4QueuesSidebarProductionised
		? isGetNpleDisabledLoadingNew
		: isGetNpleDisabledLoadingOld;

	return [npleDisabled, isGetNpleDisabledLoading];
};

export const useIsGettingStartedPageEnabled = (customContext?: UseResourceCustomContext) => {
	const { data, loading: projectContextLoading } = useProjectContext(customContext) || {};
	const isNpleProductionised = ff('getting_started_page_production_rollout');
	const { simplified: isSimplified = false, isProjectAdmin, projectKey, projectId } = data || {};

	const [isLoading, isExcludedProject] = useIsGettingStartedPageExcludedProject(
		projectId?.toString(),
		customContext,
	);
	const [npleDisabled, isGetNpleDisabledLoading] = useGetNpleDisabledProjectPropertyWithFF(
		projectKey,
		customContext,
	);

	const isDataLoading =
		isLoading ||
		(projectContextLoading && fg('jsm-wait-for-project-context-in-nple')) ||
		(isGetNpleDisabledLoading && isNpleProductionised);

	const { appPermissions } = useTenantContext() || {};

	const hasJSMAccess = fg('add-product-permission-check-for-nple')
		? appPermissions?.hasServiceDeskAccess
		: true;

	return useMemo(
		() => [
			isDataLoading,
			!isDataLoading
				? hasJSMAccess &&
					isProjectAdmin &&
					!isSimplified &&
					!isExcludedProject &&
					!npleDisabled &&
					isNpleProductionised
				: false,
		],
		[
			isDataLoading,
			isProjectAdmin,
			isSimplified,
			isExcludedProject,
			npleDisabled,
			isNpleProductionised,
			hasJSMAccess,
		],
	);
};
